import { useEffect, useMemo, useState } from "react";
import Modal from "@mui/material/Modal";
import SupportIcon from "../Assets/Icons/SupportIcon";
import { TextField, MenuItem } from "@mui/material";
import AgricultureIcon from "@mui/icons-material/Agriculture";
import { useReferenceData } from "../hooks/useReferenceData";
import { buildSparsePayload, toOptionalNumber } from "../utils/sparsePayload";
import { hasComparableFormChanges } from "../utils/formComparison";
import MainButton from "./Buttons/MainButton";
import ModalShell, { ModalTitle } from "./ModalShell";

function NewMachineModal({
  open,
  handleClose,
  handleEditMachine,
  machineState,
}) {
  const { referenceData } = useReferenceData(["machine_types"]);
  const [newMachineName, setNewMachineName] = useState(machineState?.name || "");
  const [newMachineType, setNewMachineType] = useState(machineState?.type || "");
  const [newMachineHorsepower, setNewMachineHorsepower] = useState(
    machineState?.horsepower ?? ""
  );
  const [newMachineDefaultAcreHour, setNewMachineDefaultAcreHour] = useState(
    machineState?.default_acre_hour ?? ""
  );

  useEffect(() => {
    setNewMachineName(machineState?.name || "");
    setNewMachineType(machineState?.type || "");
    setNewMachineHorsepower(machineState?.horsepower ?? "");
    setNewMachineDefaultAcreHour(machineState?.default_acre_hour ?? "");
  }, [machineState, open]);

  const isSaveDisabled = useMemo(() => {
    const currentPayload = buildSparsePayload({
      type: newMachineType,
      name: newMachineName,
      horsepower: toOptionalNumber(newMachineHorsepower),
      default_acre_hour: toOptionalNumber(newMachineDefaultAcreHour),
    });
    const baselinePayload = buildSparsePayload({
      type: machineState?.type,
      name: machineState?.name,
      horsepower: toOptionalNumber(machineState?.horsepower),
      default_acre_hour: toOptionalNumber(machineState?.default_acre_hour),
    });

    return !hasComparableFormChanges(currentPayload, baselinePayload);
  }, [
    machineState?.default_acre_hour,
    machineState?.horsepower,
    machineState?.name,
    machineState?.type,
    newMachineDefaultAcreHour,
    newMachineHorsepower,
    newMachineName,
    newMachineType,
  ]);

  return (
    <Modal
      open={open}
      onClose={() => handleClose(false)}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <ModalShell
        accent="info"
        icon={<SupportIcon color={"#FFFFFF"} width="38px" height="38px" />}
        onClose={() => handleClose(false)}
        width="min(620px, calc(100vw - 32px))"
      >
          <ModalTitle accent="info" className="text-[20px]">
            Edit the details of this machine. <AgricultureIcon />
          </ModalTitle>
          <div className="grid gap-4 md:grid-cols-2">
            <TextField
              id="standard-basic"
              label={`Type`}
              variant="outlined"
              select
              fullWidth
              value={newMachineType}
              onChange={(e) => setNewMachineType(e.target.value)}
            >
              {(referenceData.machine_types || []).map((machineTypeOption) => (
                <MenuItem
                  key={machineTypeOption.value}
                  value={machineTypeOption.value}
                >
                  {machineTypeOption.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              id="standard-basic"
              label={`Name`}
              variant="outlined"
              fullWidth
              value={newMachineName}
              onChange={(e) => setNewMachineName(e.target.value)}
            />
            <TextField
              id="standard-basic"
              label="Horse Power (HP)"
              fullWidth
              type="number"
              InputProps={{ inputProps: { min: 0 } }}
              variant="outlined"
              value={newMachineHorsepower}
              onChange={(e) => setNewMachineHorsepower(e.target.value)}
            />
            <TextField
              id="standard-basic"
              label="Default ac/hr"
              variant="outlined"
              fullWidth
              type="number"
              InputProps={{ inputProps: { min: 0 } }}
              value={newMachineDefaultAcreHour}
              onChange={(e) => setNewMachineDefaultAcreHour(e.target.value)}
            />
          </div>
          <div className="mt-6 flex w-full justify-center gap-4">
            <MainButton
              onClick={() => {
                handleClose(false);
                handleEditMachine({
                  type: newMachineType,
                  name: newMachineName,
                  horsepower: toOptionalNumber(newMachineHorsepower),
                  default_acre_hour: toOptionalNumber(newMachineDefaultAcreHour),
                });
              }}
              text="Save Changes"
              disabled={isSaveDisabled}
            />
            <MainButton
              onClick={() => handleClose(false)}
              secondary={true}
              text="Cancel"
            />
          </div>
      </ModalShell>
    </Modal>
  );
}

export default NewMachineModal;
